/**
 * Form 1: Business Basics
 * 
 * Collects fundamental business information for pitch deck generation.
 * Includes real-time validation, word counts, and autosave.
 */

import { useState, useCallback } from "react";
import { Check, AlertCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  Form1Data,
  countWords,
  validateEmail,
  validateTextLength,
  validatePhone,
  validateWordCount,
  validateTeamMembers,
  validateForm1,
} from "@/hooks/usePitchDeckForm";

interface PitchDeckForm1Props {
  formData: Form1Data;
  updateField: <K extends keyof Form1Data>(field: K, value: Form1Data[K]) => void;
  errors: Partial<Record<keyof Form1Data, string>>;
  setErrors: React.Dispatch<React.SetStateAction<Partial<Record<string, string>>>>;
  onSave: (showMessage?: boolean, markComplete?: boolean) => Promise<boolean>;
  onContinue: () => void;
  isSaving: boolean;
  lastSaved: Date | null;
  hasUnsavedChanges: boolean;
}

interface FormFieldProps {
  label: string;
  error?: string;
  isValid?: boolean;
  required?: boolean;
  children: React.ReactNode;
  helperText?: React.ReactNode;
}

function FormField({ label, error, isValid, required, children, helperText }: FormFieldProps) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Label className="font-semibold">
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </Label>
        {isValid && !error && (
          <Check className="h-4 w-4 text-green-600" aria-label="Valid" />
        )}
      </div>
      {children}
      {helperText && (
        <div className="text-sm text-muted-foreground">{helperText}</div>
      )}
      {error && (
        <div className="flex items-center gap-2 text-sm text-destructive" role="alert">
          <AlertCircle className="h-4 w-4" />
          {error}
        </div>
      )}
    </div>
  );
}

function WordCountIndicator({ text, min, max }: { text: string; min: number; max: number }) {
  const count = countWords(text);
  const isValid = count >= min && count <= max;
  const isEmpty = count === 0;

  return (
    <span
      className={cn(
        "text-sm",
        isEmpty && "text-muted-foreground",
        !isEmpty && isValid && "text-green-600",
        !isEmpty && !isValid && "text-destructive"
      )}
    >
      Word count: {count}/{max}
    </span>
  );
}

export function PitchDeckForm1({
  formData,
  updateField,
  errors,
  setErrors,
  onSave,
  onContinue,
  isSaving,
  lastSaved,
  hasUnsavedChanges,
}: PitchDeckForm1Props) {
  const [touched, setTouched] = useState<Partial<Record<keyof Form1Data, boolean>>>({});

  const handleBlur = useCallback((field: keyof Form1Data) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    
    // Validate the specific field
    let error: string | null = null;
    
    switch (field) {
      case "email":
        error = validateEmail(formData.email);
        break;
      case "full_name":
        error = validateTextLength(formData.full_name, "Name", 2, 100);
        break;
      case "address":
        error = validateTextLength(formData.address, "Address", 10, 200);
        break;
      case "phone":
        error = validatePhone(formData.phone);
        break;
      case "company_name":
        error = validateTextLength(formData.company_name, "Company name", 2, 100);
        break;
      case "problem_statement":
        error = validateWordCount(formData.problem_statement, "Problem statement", 10, 50);
        break;
      case "solution_statement":
        error = validateWordCount(formData.solution_statement, "Solution statement", 10, 50);
        break;
      case "team_members":
        error = validateTeamMembers(formData.team_members);
        break;
    }
    
    setErrors((prev) => {
      const next = { ...prev };
      if (error) {
        next[field] = error;
      } else {
        delete next[field];
      }
      return next;
    });
  }, [formData, setErrors]);

  const isFieldValid = (field: keyof Form1Data): boolean => {
    if (!touched[field]) return false;
    return !errors[field] && !!formData[field];
  };

  const handleSaveDraft = async () => {
    const validationErrors = validateForm1(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setTouched({
        email: true,
        full_name: true,
        address: true,
        phone: true,
        company_name: true,
        problem_statement: true,
        solution_statement: true,
        team_members: true,
      });
      return;
    }
    await onSave(true, false);
  };

  const handleContinue = async () => {
    // Validate all fields
    const validationErrors = validateForm1(formData);
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setTouched({
        email: true,
        full_name: true,
        address: true,
        phone: true,
        company_name: true,
        problem_statement: true,
        solution_statement: true,
        team_members: true,
      });
      return;
    }

    // Save and mark form 1 as complete
    const saved = await onSave(false, true);
    if (saved) {
      onContinue();
    }
  };

  const formatLastSaved = (date: Date) => {
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const isFormValid = Object.keys(validateForm1(formData)).length === 0;

  return (
    <form className="space-y-6" onSubmit={(e) => e.preventDefault()}>
      {/* Contact Information Section */}
      <div className="space-y-6">
        <h3 className="text-lg font-semibold text-foreground">Contact Information</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField
            label="Email Address"
            error={touched.email ? errors.email : undefined}
            isValid={isFieldValid("email")}
            required
          >
            <Input
              type="email"
              placeholder="your.email@example.com"
              value={formData.email}
              onChange={(e) => updateField("email", e.target.value)}
              onBlur={() => handleBlur("email")}
              className={cn(
                touched.email && errors.email && "border-destructive",
                isFieldValid("email") && "border-green-500"
              )}
              aria-required="true"
            />
          </FormField>

          <FormField
            label="Full Name"
            error={touched.full_name ? errors.full_name : undefined}
            isValid={isFieldValid("full_name")}
            required
          >
            <Input
              type="text"
              placeholder="John Smith"
              value={formData.full_name}
              onChange={(e) => updateField("full_name", e.target.value)}
              onBlur={() => handleBlur("full_name")}
              className={cn(
                touched.full_name && errors.full_name && "border-destructive",
                isFieldValid("full_name") && "border-green-500"
              )}
              aria-required="true"
            />
          </FormField>
        </div>

        <FormField
          label="Business Address"
          error={touched.address ? errors.address : undefined}
          isValid={isFieldValid("address")}
          required
        >
          <Input
            type="text"
            placeholder="123 Main St, City, State, ZIP"
            value={formData.address}
            onChange={(e) => updateField("address", e.target.value)}
            onBlur={() => handleBlur("address")}
            className={cn(
              touched.address && errors.address && "border-destructive",
              isFieldValid("address") && "border-green-500"
            )}
            aria-required="true"
          />
        </FormField>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField
            label="Phone Number"
            error={touched.phone ? errors.phone : undefined}
            isValid={isFieldValid("phone")}
            required
          >
            <Input
              type="tel"
              placeholder="(555) 123-4567"
              value={formData.phone}
              onChange={(e) => updateField("phone", e.target.value)}
              onBlur={() => handleBlur("phone")}
              className={cn(
                touched.phone && errors.phone && "border-destructive",
                isFieldValid("phone") && "border-green-500"
              )}
              aria-required="true"
            />
          </FormField>

          <FormField
            label="Company Name"
            error={touched.company_name ? errors.company_name : undefined}
            isValid={isFieldValid("company_name")}
            required
          >
            <Input
              type="text"
              placeholder="Your Company LLC"
              value={formData.company_name}
              onChange={(e) => updateField("company_name", e.target.value)}
              onBlur={() => handleBlur("company_name")}
              className={cn(
                touched.company_name && errors.company_name && "border-destructive",
                isFieldValid("company_name") && "border-green-500"
              )}
              aria-required="true"
            />
          </FormField>
        </div>
      </div>

      {/* Business Description Section */}
      <div className="space-y-6 pt-4 border-t">
        <h3 className="text-lg font-semibold text-foreground">Business Description</h3>

        <FormField
          label="Problem Statement"
          error={touched.problem_statement ? errors.problem_statement : undefined}
          isValid={isFieldValid("problem_statement")}
          required
          helperText={
            <WordCountIndicator text={formData.problem_statement} min={10} max={50} />
          }
        >
          <Textarea
            placeholder="Describe the problem your business solves (10-50 words)"
            value={formData.problem_statement}
            onChange={(e) => updateField("problem_statement", e.target.value)}
            onBlur={() => handleBlur("problem_statement")}
            rows={4}
            className={cn(
              touched.problem_statement && errors.problem_statement && "border-destructive",
              isFieldValid("problem_statement") && "border-green-500"
            )}
            aria-required="true"
          />
        </FormField>

        <FormField
          label="Solution Statement"
          error={touched.solution_statement ? errors.solution_statement : undefined}
          isValid={isFieldValid("solution_statement")}
          required
          helperText={
            <WordCountIndicator text={formData.solution_statement} min={10} max={50} />
          }
        >
          <Textarea
            placeholder="Describe how your business solves this problem (10-50 words)"
            value={formData.solution_statement}
            onChange={(e) => updateField("solution_statement", e.target.value)}
            onBlur={() => handleBlur("solution_statement")}
            rows={4}
            className={cn(
              touched.solution_statement && errors.solution_statement && "border-destructive",
              isFieldValid("solution_statement") && "border-green-500"
            )}
            aria-required="true"
          />
        </FormField>
      </div>

      {/* Team Section */}
      <div className="space-y-6 pt-4 border-t">
        <h3 className="text-lg font-semibold text-foreground">Team Information</h3>

        <FormField
          label="Team Members"
          error={touched.team_members ? errors.team_members : undefined}
          isValid={isFieldValid("team_members")}
          required
          helperText="List key team members and their roles, one per line"
        >
          <Textarea
            placeholder={`John Smith - CEO\nJane Doe - CTO\nBob Johnson - CFO`}
            value={formData.team_members}
            onChange={(e) => updateField("team_members", e.target.value)}
            onBlur={() => handleBlur("team_members")}
            rows={5}
            className={cn(
              touched.team_members && errors.team_members && "border-destructive",
              isFieldValid("team_members") && "border-green-500"
            )}
            aria-required="true"
          />
        </FormField>
      </div>

      {/* Save Status & Actions */}
      <div className="pt-6 border-t space-y-4">
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <div>
            {isSaving ? (
              <span>Saving...</span>
            ) : lastSaved ? (
              <span>Last saved at {formatLastSaved(lastSaved)}</span>
            ) : hasUnsavedChanges ? (
              <span className="text-warning">Unsaved changes</span>
            ) : null}
          </div>
        </div>

        <div className="flex flex-col-reverse sm:flex-row gap-4 sm:justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={handleSaveDraft}
            disabled={isSaving}
          >
            Save Draft
          </Button>
          <Button
            type="button"
            onClick={handleContinue}
            disabled={isSaving || !isFormValid}
            className="sm:min-w-[180px]"
          >
            Continue to Form 2
          </Button>
        </div>
      </div>
    </form>
  );
}
